import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle, Zap, Eye } from "lucide-react";
import PayPalEmailWarning from "@/components/PayPalEmailWarning";
import { useState, useEffect } from "react";
import { api } from "@/App";

// PayPal NCP hosted button for the $25 Profile Boost
const BOOST_PAYPAL_URL = "https://www.paypal.com/ncp/payment/M6Q3CR4HPT6LJ";

export const BoostDialog = ({ open, onOpenChange, boostStatus }) => {
  const buttonReady = BOOST_PAYPAL_URL.startsWith("http");
  const [analytics, setAnalytics] = useState(null);
  const hoursLeft = boostStatus?.active
    ? Math.max(1, Math.ceil((new Date(boostStatus.boosted_until) - Date.now()) / 3600000))
    : 0;

  useEffect(() => {
    if (open) {
      api.get("/boost/analytics").then((res) => setAnalytics(res.data)).catch(() => {});
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="boost-dialog" className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-amber-400" />
            Boost Your Profile
          </DialogTitle>
          <DialogDescription>
            Get featured at the top of Discover for 24 hours
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {boostStatus?.active && (
            <div
              className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-center text-sm text-amber-400"
              data-testid="boost-active-banner"
            >
              ⚡ Your boost is active — about {hoursLeft}h left. Buying again adds another 24 hours.
            </div>
          )}

          {analytics && analytics.boost_count > 0 && (
            <div className="bg-white/5 border border-white/10 rounded-lg p-4" data-testid="boost-analytics">
              <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5 mb-3">
                <Eye className="w-3.5 h-3.5 text-amber-400" /> Your boost results
              </p>
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-xl font-bold text-amber-400" data-testid="boosted-views-count">{analytics.boosted_views}</p>
                  <p className="text-[11px] text-muted-foreground leading-tight">views while boosted</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-green-400" data-testid="extra-views-count">+{analytics.extra_views}</p>
                  <p className="text-[11px] text-muted-foreground leading-tight">extra vs normal days</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-white">{analytics.avg_daily_normal}</p>
                  <p className="text-[11px] text-muted-foreground leading-tight">views/day unboosted</p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-amber-500/10 rounded-lg p-4 text-center">
            <p className="text-4xl font-bold text-amber-400 mb-2">$25</p>
            <p className="text-sm text-muted-foreground">24-hour Boost</p>
          </div>

          <div className="space-y-2 text-sm">
            <p className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
              <span>Your profile is pinned to the top of your matches' Discover feed</span>
            </p>
            <p className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
              <span>Stand out with a "Boosted" badge on your profile card</span>
            </p>
            <p className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5" />
              <span>Boosts stack — buy again to extend by another 24 hours</span>
            </p>
          </div>

          <div className="text-center">
            <PayPalEmailWarning />
            <p className="text-sm text-muted-foreground mb-4 mt-3">
              Payment via PayPal ($27 total: $25 boost + $2 handling fee)
            </p>

            {buttonReady ? (
              <div className="flex justify-center">
                <style>{`.pp-boost-btn{text-align:center;border:none;border-radius:0.25rem;min-width:11.625rem;padding:0 2rem;height:2.625rem;font-weight:bold;background-color:#FFD140;color:#000000;font-family:"Helvetica Neue",Arial,sans-serif;font-size:1rem;line-height:1.25rem;cursor:pointer;}`}</style>
                <form
                  action={BOOST_PAYPAL_URL}
                  method="post"
                  target="_blank"
                  style={{ display: "inline-grid", justifyItems: "center", alignContent: "start", gap: "0.5rem" }}
                >
                  <input className="pp-boost-btn" type="submit" value="Boost Now" data-testid="boost-pay-btn" />
                  <img src="https://www.paypalobjects.com/images/Debit_Credit_APM.svg" alt="cards" />
                  <section style={{ fontSize: "0.75rem" }}>
                    Powered by <img src="https://www.paypalobjects.com/paypal-ui/logos/svg/paypal-wordmark-color.svg" alt="paypal" style={{ height: "0.875rem", verticalAlign: "middle" }} />
                  </section>
                </form>
              </div>
            ) : (
              <div
                className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm text-amber-400"
                data-testid="boost-button-pending"
              >
                The PayPal payment button is being configured — check back shortly.
              </div>
            )}

            <p className="text-xs text-muted-foreground mt-4">
              After payment, return to this page. Your boost activates within 30 seconds.
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="w-full"
            data-testid="boost-dialog-close-btn"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BoostDialog;
